import type { BestDecisionsResult } from "@beyourbest/decision-engine";
import type { ReplayData } from "@Shared/types/ReplayData";

export type ReplayDecision = BestDecisionsResult;
export type ReplayDecisionOption = ReplayDecision["decisions"]["top"][number];
export type ReplayPassType = ReplayDecisionOption["passType"];

export const ReplayPassType = {
  GROUND: "ground" as ReplayPassType,
  THROUGH: "through" as ReplayPassType,
  LOFT: "loft" as ReplayPassType,
  CROSS: "cross" as ReplayPassType,
  SHOT: "shot" as ReplayPassType,
  TURNOVER: "turnover" as ReplayPassType,
  DRIBBLE: "dribble" as ReplayPassType,
} as const;

type ReplayPreviewPayload = {
  time: number;
  ballPosition: { x: number; y: number; z: number };
  option: {
    startPosition: { x: number; y: number; z: number };
    passPosition: { x: number; y: number; z: number };
    receiverPosition: { x: number; y: number; z: number };
    passType?: ReplayPassType;
    score: number;
  };
};

// To match what is done on VR
export const mapAnalyzerPositionToReplay = (pos: { x: number; y: number; z?: number }) => ({
  x: (pos.y - 34) / 0.9,
  y: pos.z ?? 0,
  z: (pos.x - 52.5) / 0.93,
});

export const getDecisionOptionId = (decision: ReplayDecisionOption) =>
  `${decision.targetPlayerId}:${decision.passType}:${decision.endPosition.x.toFixed(3)}:${decision.endPosition.y.toFixed(3)}`;

export const getDecisionOptionTime = (option: ReplayDecisionOption | null | undefined, fallbackTime: number) => option?.evalTime ?? fallbackTime;

export const getPrimaryDecisionOption = (decision: ReplayDecision): ReplayDecisionOption | null => {
  return decision.decisions.user ?? decision.decisions.top[0] ?? null;
};

export const getReplayDecisionPassType = (decision: ReplayDecision): ReplayPassType | null => {
  return getPrimaryDecisionOption(decision)?.passType ?? null;
};

export const filterReplayDecisions = (decisions: ReplayDecision[], activeFilter: ReplayPassType | null) => {
  if (!activeFilter) {
    return decisions;
  }

  return decisions.filter(decision => getReplayDecisionPassType(decision) === activeFilter);
};

export const getReplayDecisionFilterOptions = (decisions: ReplayDecision[]) => {
  const counts = new Map<ReplayPassType | null, number>([[null, decisions.length]]);

  decisions.forEach(decision => {
    const passType = getReplayDecisionPassType(decision);
    if (!passType) return;

    counts.set(passType, (counts.get(passType) ?? 0) + 1);
  });

  return [
    {
      value: null,
      labelKey: "session-results.decision-filter.all",
      count: decisions.length,
    },
    ...Object.values(ReplayPassType).map(passType => ({
      value: passType,
      labelKey: `session-results.decision-filter.${passType}`,
      count: counts.get(passType) ?? 0,
    })),
  ];
};

export const getAlternativeDecisions = (decision: ReplayDecision): ReplayDecisionOption[] => {
  const userDecision = decision.decisions.user;
  const userDecisionId = userDecision ? getDecisionOptionId(userDecision) : null;

  return decision.decisions.top.filter(option => getDecisionOptionId(option) !== userDecisionId).slice(0, 2);
};

export const getDisplayedDecisionOptions = (decision: ReplayDecision) => {
  const userOption = decision.decisions.user ?? null;
  const alternativeOptions = getAlternativeDecisions(decision);
  const displayedOptions = [userOption, ...alternativeOptions];
  const validOptions = displayedOptions.filter((option): option is ReplayDecisionOption => option !== null);
  const selectedOption = displayedOptions[0] ?? alternativeOptions[0] ?? null;
  const maxScore = Math.max(0, ...displayedOptions.map(option => option?.displayScore ?? 0));

  return {
    didUserMakeDecision: Boolean(userOption),
    userOption,
    alternativeOptions,
    displayedOptions,
    validOptions,
    selectedOption,
    maxScore,
  };
};

export const getSelectedDecisionOption = (decision: ReplayDecision, selectedCard: number) => {
  const { displayedOptions, alternativeOptions } = getDisplayedDecisionOptions(decision);

  return displayedOptions[selectedCard] ?? alternativeOptions[0] ?? null;
};

export const getDecisionCardTime = (decision: ReplayDecision, index: number) => {
  const option = index === 0 ? decision.decisions.user : getAlternativeDecisions(decision)[index - 1];
  return getDecisionOptionTime(option, decision.startTime);
};

export const getFirstUserDecision = (decisions: ReplayDecision[]) => {
  return decisions[0]?.decisions.user ?? null;
};

export const hasBetterReplayDecision = (decisions: ReplayDecision[]): boolean =>
  decisions.some(({ decisions: options }) => {
    const userScore = options.user?.displayScore ?? 0;
    return Number.isFinite(userScore) && options.top.some(option => Number.isFinite(option.displayScore) && option.displayScore > userScore);
  });

export const getFirstDecisionScore = (decisions: ReplayDecision[]): number | null => {
  const userScore = getFirstUserDecision(decisions)?.displayScore;
  if (userScore === undefined || !Number.isFinite(userScore)) return null;
  const maxScore = Math.max(0, userScore, ...decisions[0].decisions.top.map(option => option.displayScore));
  return maxScore > 0 && Number.isFinite(maxScore) ? Math.round((userScore / maxScore) * 100) : 0;
};

const getBallPositionAtTime = (replayData: ReplayData, time: number) => {
  const positions = replayData.ball?.position ?? [];

  if (positions.length === 0) {
    return null;
  }

  let closestPosition = positions[0];

  for (const position of positions) {
    if (position.time > time) {
      break;
    }

    closestPosition = position;
  }

  return closestPosition;
};

const getFallbackBallPosition = (startReplay: ReturnType<typeof mapAnalyzerPositionToReplay>) => ({
  x: startReplay.z,
  y: 0,
  z: startReplay.x,
});

export const buildReplayPreviewPayload = (option: ReplayDecisionOption, replayData: ReplayData, fallbackTime: number): ReplayPreviewPayload => {
  const decisionTime = getDecisionOptionTime(option, fallbackTime);
  const ballPosition = getBallPositionAtTime(replayData, decisionTime);
  const startReplay = mapAnalyzerPositionToReplay(option.startPosition);
  const endReplay = mapAnalyzerPositionToReplay(option.endPosition);
  const receiverReplay = mapAnalyzerPositionToReplay(option.receiverCurrentPosition ?? option.endPosition);

  return {
    time: decisionTime,
    ballPosition: ballPosition ?? getFallbackBallPosition(startReplay),
    option: {
      startPosition: {
        x: startReplay.z,
        y: 0,
        z: startReplay.x,
      },
      passPosition: {
        x: endReplay.z,
        y: 0,
        z: endReplay.x,
      },
      receiverPosition: {
        x: receiverReplay.z,
        y: 0,
        z: receiverReplay.x,
      },
      passType: option.passType,
      score: option.displayScore,
    },
  };
};
